import {Accordion, AccordionDetails, AccordionSummary, Card, Grid, Typography} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {SelectInputElement} from '../Components/SelectInputElement';
import React from 'react';
import {observer} from 'mobx-react-lite';
import {HealthQuestionsProps} from './HealthQuestionsList';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/HealthXref';

const BoneMuscleJoint = observer(({counter, valueChanged, parameters, coreQuestions}: HealthQuestionsProps) => {
    return (
        <Accordion style={{width: '100%'}} TransitionProps={{unmountOnExit: true}}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={'bonemuscle-content'}
                id={'bonemuscle-header'}
            >
                <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <div>Bone, muscle, joint, thyroid, organ, or gland disorder</div>
                    <div>{counter('bonemuscle')}</div>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid key="params" container>
                        {coreQuestions?.map(({name, label}) => (
                            <Grid item xs={4} key={name}>
                                <SwitchSegmentedYesNo
                                    param={{
                                        name: name,
                                        label: label,
                                        category: 'bonemuscle',
                                        ...(parameters[name as keyof typeof parameters] as Parameter),
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        ))}
                        {parameters.OsteoArthritis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Osteo - Arthritis</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label={'When were you diagnosed?'}
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.osteoarthritisDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.osteoarthritisDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'osteoarthritisTreatment'}
                                                label={
                                                    'Provide details on the current treatment and/or medications you are taking for this condition.'
                                                }
                                                value={parameters.osteoarthritisTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.osteoarthritisTreatment, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'osteoarthritisSideEffects',
                                                    label: 'Does your arthritis cause any limitations to activities of daily living?',
                                                    ...parameters.osteoarthritisSideEffects,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.RheumatoidArthritis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Rheumatoid Arthritis</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.rheumatoidarthritisDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.rheumatoidarthritisDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'rhuematoidarthritisImmunoMedication',
                                                    label: 'Are you taking an immunosupressant (humira or eliquis)?',
                                                    ...parameters.rhuematoidarthritisImmunoMedication,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'rheumatoidarthritisSideEffects',
                                                    label: 'Does your arthritis cause any limitations to activities of daily living?',
                                                    ...parameters.rheumatoidarthritisSideEffects,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="rheumatoidarthritisTreatment"
                                                label="Provide details on the current treatment and/or medications you are taking for this condition."
                                                value={parameters.rheumatoidarthritisTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.rheumatoidarthritisTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Osteoperosis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Osteoperosis</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.osteoperosisDiagnosisDate?.value || null}
                                                    onChange={(value) =>
                                                        valueChanged(parameters.osteoperosisDiagnosisDate, value)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'osteoperosisConditions',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results, referral to hospital, surgery?',
                                                    ...parameters.osteoperosisConditions,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="osteoperosisTreatment"
                                                label="Provide details on all pending evaluations and treatment related to this condition."
                                                value={parameters.osteoperosisTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.osteoperosisTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.EnlargedProstateBenignProstaticHyperplasia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Enlarged Prostate / Benign Prostatic Hyperplasia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.enlargedprostateDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.enlargedprostateDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'enlargedprostatePendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.enlargedprostatePendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Was it diagnosed as?',
                                                    name: 'enlargedprostateCancerous',
                                                    options: fieldOptions.enlargedprostateCancerous.options,
                                                    ...parameters.enlargedprostateCancerous,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="enlargedprostateEvaluations"
                                                label="Provide details on all pending evaluations and treatment related to this condition., Has a biopsy been completed due to this condition?"
                                                value={parameters.enlargedprostateEvaluations?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.enlargedprostateEvaluations, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Were the biopsy results?',
                                                    name: 'enlargedprostateDiagnosis',
                                                    options: fieldOptions.enlargedprostateDiagnosis.options,
                                                    ...parameters.enlargedprostateDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="enlargedprostateTreatment"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.enlargedprostateTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.enlargedprostateTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Gout.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Gout</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.goutDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.goutDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'goutPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigation, test results, referral to hospital, surgery?',
                                                    ...parameters.goutPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="goutTreatmentPlan"
                                                label="Provide details on all pending evaluations and treatment related to this condition."
                                                value={parameters.goutTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.goutTreatmentPlan, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.MSMultipleSclerosis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>MS (Multiple Sclerosis)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="multiplesclerosisDiagnosis"
                                                label="How long ago were you diagnosed with MS"
                                                value={parameters.multiplesclerosisDiagnosis?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.multiplesclerosisDiagnosis, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'multiplesclerosisDailyLivingActivities',
                                                    label: 'Has MS affected your activities of daily living?',
                                                    ...parameters.multiplesclerosisDailyLivingActivities,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="multiplesclerosisTreatment"
                                                label="Provide details including medications you take, the degree of your symptoms, and how this affects your daily activities."
                                                value={parameters.multiplesclerosisTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.multiplesclerosisTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Fibromyalgia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Fibromyalgia</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.fibromyalgiaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.fibromyalgiaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'fibromyalgiaPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.fibromyalgiaPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'fibromyalgiaSideEffects',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.fibromyalgiaSideEffects,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Hyperthyroidism.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Hyperthyroidism</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.thyroidDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.thyroidDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'thyroidMalignant',
                                                    label: 'Any history of thyroid cancer?',
                                                    ...parameters.thyroidMalignant,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'thyroidPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital?',
                                                    ...parameters.thyroidPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="thyroidSymptoms"
                                                label="Are you currently experiencing symptoms related to this condition?, When did you last experience symptoms related to this condition?, Provide details on all pending evaluations and treatment related to this condition."
                                                value={parameters.thyroidSymptoms?.value}
                                                onChange={(value) => valueChanged(parameters.thyroidSymptoms, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Hypothyroidism.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Hypothyroidism</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.hypothyroidismDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.hypothyroidismDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hypthyroidismMalignant',
                                                    label: 'Any history of thyroid cancer?',
                                                    ...parameters.hypthyroidismMalignant,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hypothyroidismPendingDiagnosis',
                                                    label: 'Apart from your regular follow ups for this condition are you awaiting further medical tests, investigations or test results?',
                                                    ...parameters.hypothyroidismPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hypothyroidismMedications"
                                                label="What medications have been prescribed in the last 12 months?"
                                                value={parameters.hypothyroidismMedications?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.hypothyroidismMedications, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.LiverDisease.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Liver Disease</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={4}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'liverdiseaseDiagnosis',
                                                    label: 'Have you been diagnosed with liver disease? ',
                                                    ...parameters.liverdiseaseDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'liverdiseaseBiopsy',
                                                    label: 'Was a liver biopsy performed?,  ',
                                                    ...parameters.liverdiseaseBiopsy,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="liverdiseaseTreatment"
                                                label="What treatment was prescribed or completed for this condition? "
                                                value={parameters.liverdiseaseTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.liverdiseaseTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Lupus.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Lupus</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?,  "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.lupusDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.lupusDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'lupusSideEffects',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.lupusSideEffects,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'lupusRecovery',
                                                    label: 'Have you had a full recovery with no complications? ',
                                                    ...parameters.lupusRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="lupusComplications"
                                                label="Provide details of your complications. "
                                                value={parameters.lupusComplications?.value}
                                                onChange={(value) => valueChanged(parameters.lupusComplications, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="lupusTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.lupusTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.lupusTreatmentPlan, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
});

export default BoneMuscleJoint;
